import { useState, useEffect } from "react";
import { getPhrasesForLanguage, type PhraseCategory, type Phrase } from "../content/speakingPhrases.js";
import { AudioButton } from "./AudioButton.js";
import { recordSkillAttempt } from "../utils/progress.js";
import { sound } from "../utils/garden-sound.js";

const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accentBg: 'var(--accent-bg)', accentBorder: 'var(--accent-border)',
};
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

const LANG_LABEL: Record<string, string> = {
  es: 'Spanish', pt: 'Portuguese', fi: 'Finnish', fr: 'French', it: 'Italian',
};

type PhraseStatus = "unknown" | "learning" | "known";

function loadPhraseStatus(languageCode: string): Record<string, PhraseStatus> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(`conjugr8.phrases.${languageCode}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function savePhraseStatus(languageCode: string, statuses: Record<string, PhraseStatus>) {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(`conjugr8.phrases.${languageCode}`, JSON.stringify(statuses)); } catch {}
}

function phraseKey(categoryId: string, phraseIndex: number): string {
  return `${categoryId}:${phraseIndex}`;
}

export function PhrasePractice({ languageCode }: { languageCode: string }) {
  const categories = getPhrasesForLanguage(languageCode);
  const [activeCategoryId, setActiveCategoryId] = useState(categories[0]?.id ?? "greetings");
  const [statuses, setStatuses] = useState<Record<string, PhraseStatus>>({});
  const [cardIndex, setCardIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [mode, setMode] = useState<"browse" | "drill">("browse");

  useEffect(() => { setStatuses(loadPhraseStatus(languageCode)); }, [languageCode]);
  useEffect(() => { setCardIndex(0); setRevealed(false); }, [activeCategoryId, mode]);

  const activeCategory = categories.find((c) => c.id === activeCategoryId) ?? categories[0];
  const phrases = activeCategory?.phrases ?? [];

  const drillQueue = mode === "drill"
    ? [
        ...phrases.filter((_, i) => (statuses[phraseKey(activeCategoryId, i)] ?? "unknown") !== "known"),
        ...phrases.filter((_, i) => (statuses[phraseKey(activeCategoryId, i)] ?? "unknown") === "known"),
      ]
    : phrases;

  const currentPhrase = drillQueue[cardIndex];
  const totalPhrases = phrases.length;
  const knownCount = phrases.filter((_, i) => statuses[phraseKey(activeCategoryId, i)] === "known").length;
  const learningCount = phrases.filter((_, i) => statuses[phraseKey(activeCategoryId, i)] === "learning").length;

  const allPhraseKeys = categories.flatMap((cat) => cat.phrases.map((_, i) => phraseKey(cat.id, i)));
  const totalKnown = allPhraseKeys.filter((k) => statuses[k] === "known").length;
  const totalAll = allPhraseKeys.length;

  function setStatus(status: PhraseStatus) {
    if (!currentPhrase) return;
    const originalIndex = phrases.indexOf(currentPhrase);
    if (originalIndex === -1) return;
    const key = phraseKey(activeCategoryId, originalIndex);
    const updated = { ...statuses, [key]: status };
    setStatuses(updated);
    savePhraseStatus(languageCode, updated);
    // Self-assessment feeds the Speaking competence: "known" counts as a hit.
    if (status === "known") sound.correct(0);
    else if (status === "unknown") sound.miss();
    if (status !== "unknown") {
      recordSkillAttempt({ timestamp: Date.now(), category: "speaking", status: status === "known" ? "correct" : "incorrect" });
    }
    nextCard();
  }

  function nextCard() { setRevealed(false); setCardIndex((i) => (i + 1 < drillQueue.length ? i + 1 : 0)); }
  function prevCard() { setRevealed(false); setCardIndex((i) => (i - 1 >= 0 ? i - 1 : drillQueue.length - 1)); }

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (!currentPhrase) return
      if (!revealed) {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setRevealed(true) }
        if (e.key === 'ArrowLeft') { e.preventDefault(); prevCard() }
        if (e.key === 'ArrowRight') { e.preventDefault(); nextCard() }
      } else {
        if (e.key === '1') setStatus('unknown')
        if (e.key === '2') setStatus('learning')
        if (e.key === '3') setStatus('known')
        if (e.key === 'ArrowLeft') { e.preventDefault(); prevCard() }
        if (e.key === 'ArrowRight') { e.preventDefault(); nextCard() }
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [revealed, currentPhrase, cardIndex])

  const currentOriginalIndex = currentPhrase ? phrases.indexOf(currentPhrase) : -1;
  const currentStatus: PhraseStatus = currentOriginalIndex >= 0
    ? (statuses[phraseKey(activeCategoryId, currentOriginalIndex)] ?? "unknown")
    : "unknown";

  return (
    <div>
      {/* Global progress strip */}
      {totalKnown > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 16px', borderRadius: 8, marginBottom: 20,
          background: 'var(--success-bg)', border: '1px solid var(--success-border)',
        }}>
          <span style={{ fontFamily: AF.m, fontSize: 12, color: 'var(--success)', whiteSpace: 'nowrap' }}>
            ✓ {totalKnown} / {totalAll} mastered
          </span>
          <div style={{ flex: 1, height: 4, background: C.surf2, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${(totalKnown / totalAll) * 100}%`, height: '100%', background: 'var(--success)', borderRadius: 2, transition: 'width 0.6s' }}/>
          </div>
        </div>
      )}

      {/* Category pills */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 20, paddingBottom: 2 }}>
        {categories.map((cat) => {
          const catKnown = cat.phrases.filter((_, i) => statuses[phraseKey(cat.id, i)] === "known").length;
          const isActive = cat.id === activeCategoryId;
          return (
            <button key={cat.id} onClick={() => setActiveCategoryId(cat.id)} style={{
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
              padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${isActive ? 'var(--accent)' : C.border}`,
              background: isActive ? 'var(--accent)' : 'transparent',
              color: isActive ? C.bg : C.muted,
              fontFamily: AF.b, fontSize: 13, fontWeight: isActive ? 600 : 400,
            }}>
              <span>{cat.emoji}</span>
              <span>{cat.label}</span>
              {catKnown > 0 && (
                <span style={{ fontSize: 10, opacity: 0.8 }}>{catKnown}✓</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Stats row + mode toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          {knownCount > 0 && <span style={{ fontFamily: AF.m, fontSize: 11, color: 'var(--success)' }}>✓ {knownCount} known</span>}
          {learningCount > 0 && <span style={{ fontFamily: AF.m, fontSize: 11, color: 'var(--warn)' }}>◎ {learningCount} learning</span>}
          <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>{totalPhrases - knownCount - learningCount} new</span>
        </div>
        <div style={{ display: 'flex', background: C.surf, border: `1px solid ${C.border}`, borderRadius: 7, padding: 3, gap: 1 }}>
          {(['browse', 'drill'] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)} style={{
              padding: '5px 12px', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: mode === m ? C.bg : 'transparent',
              color: mode === m ? C.text : C.muted,
              fontFamily: AF.b, fontSize: 12, fontWeight: mode === m ? 600 : 400,
              boxShadow: mode === m ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}>
              {m === 'browse' ? 'Browse' : 'Drill'}
            </button>
          ))}
        </div>
      </div>

      {/* Card */}
      {currentPhrase ? (
        <div>
          <div style={{ fontFamily: AF.m, fontSize: 11, color: C.dim, textAlign: 'center', marginBottom: 12 }}>
            {cardIndex + 1} / {drillQueue.length}
          </div>

          <div style={{
            background: C.surf, border: `1px solid ${C.border}`, borderRadius: 12,
            overflow: 'hidden', marginBottom: 12,
            cursor: revealed ? 'default' : 'pointer',
          }} onClick={() => !revealed && setRevealed(true)}>
            {/* English */}
            <div style={{ padding: '22px 24px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
                English
              </div>
              <p style={{ fontFamily: AF.h, fontSize: 22, fontWeight: 600, color: C.text, margin: 0, lineHeight: 1.3 }}>
                {currentPhrase.english}
              </p>
            </div>

            {/* Native — always rendered, opacity toggled to prevent CLS */}
            <div style={{ padding: '22px 24px', opacity: revealed ? 1 : 0, transition: 'opacity 0.2s', pointerEvents: revealed ? 'auto' : 'none' }}>
              <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
                {LANG_LABEL[languageCode] ?? languageCode.toUpperCase()}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <p style={{ fontFamily: AF.h, fontSize: 22, fontWeight: 700, color: 'var(--accent)', margin: 0, lineHeight: 1.3 }}>
                  {currentPhrase.native}
                </p>
                <AudioButton text={currentPhrase.native} size="md" />
              </div>
              {currentPhrase.tip && (
                <div style={{
                  marginTop: 14, padding: '10px 14px', borderRadius: 8,
                  background: 'var(--warn-bg)', border: '1px solid var(--warn-border)',
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                }}>
                  <span style={{ fontSize: 13, flexShrink: 0 }}>💡</span>
                  <span style={{ fontFamily: AF.b, fontSize: 12, color: 'var(--warn)', lineHeight: 1.5 }}>
                    {currentPhrase.tip}
                  </span>
                </div>
              )}
            </div>

            {/* Reveal prompt */}
            {!revealed && (
              <div style={{ padding: '0 24px 22px' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '12px', border: `1.5px dashed ${C.border}`, borderRadius: 8,
                }}>
                  <span style={{ fontFamily: AF.b, fontSize: 13, color: C.dim }}>Tap to reveal</span>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          {revealed ? (
            <div>
              <p style={{ fontFamily: AF.b, fontSize: 12, color: C.dim, textAlign: 'center', margin: '0 0 10px' }}>
                Could you say this correctly?
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { status: 'unknown' as const, label: '✗ Not yet', bg: 'var(--error-bg)', border: 'var(--error-border)', color: 'var(--error)' },
                  { status: 'learning' as const, label: '◎ Almost',  bg: 'var(--warn-bg)',  border: 'var(--warn-border)',  color: 'var(--warn)' },
                  { status: 'known'   as const, label: '✓ Got it!', bg: 'var(--success-bg)', border: 'var(--success-border)', color: 'var(--success)' },
                ].map(({ status, label, bg, border, color }) => (
                  <button key={status} onClick={() => setStatus(status)} style={{
                    flex: 1, padding: '11px 0', borderRadius: 8,
                    border: `1px solid ${border}`, background: bg, color,
                    fontFamily: AF.b, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button onClick={prevCard} style={{
                padding: '8px 18px', borderRadius: 7, border: `1px solid ${C.border}`,
                background: 'transparent', color: C.muted, fontFamily: AF.b, fontSize: 13, cursor: 'pointer',
              }}>← Prev</button>
              <span style={{
                fontFamily: AF.m, fontSize: 11,
                color: currentStatus === 'known' ? 'var(--success)' : currentStatus === 'learning' ? 'var(--warn)' : C.dim,
              }}>
                {currentStatus === 'known' ? '✓ Known' : currentStatus === 'learning' ? '◎ Learning' : 'Not started'}
              </span>
              <button onClick={nextCard} style={{
                padding: '8px 18px', borderRadius: 7, border: `1px solid ${C.border}`,
                background: 'transparent', color: C.muted, fontFamily: AF.b, fontSize: 13, cursor: 'pointer',
              }}>Next →</button>
            </div>
          )}
        </div>
      ) : (
        <p style={{ fontFamily: AF.b, fontSize: 13, color: C.dim, textAlign: 'center', padding: '32px 0' }}>
          No phrases available.
        </p>
      )}

      {/* Browse: full list */}
      {mode === 'browse' && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>
            All phrases in this category
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
            {phrases.map((phrase, i) => {
              const key = phraseKey(activeCategoryId, i);
              const s = statuses[key] ?? "unknown";
              return (
                <div key={i} onClick={() => {
                  setCardIndex(phrases.indexOf(phrase));
                  setRevealed(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '11px 16px', cursor: 'pointer',
                  borderBottom: i < phrases.length - 1 ? `1px solid ${C.border}` : 'none',
                  background: 'transparent',
                  transition: 'background 0.12s',
                }}>
                  <span style={{
                    fontFamily: AF.m, fontSize: 14, flexShrink: 0, width: 16, textAlign: 'center',
                    color: s === 'known' ? 'var(--success)' : s === 'learning' ? 'var(--warn)' : C.dim,
                  }}>
                    {s === 'known' ? '✓' : s === 'learning' ? '◎' : '·'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: AF.b, fontSize: 13, color: C.text, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {phrase.english}
                    </p>
                    <p style={{ fontFamily: AF.b, fontSize: 12, color: 'var(--accent)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {phrase.native}
                    </p>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <AudioButton text={phrase.native} size="sm" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
